import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  Button,
  Alert,
  Spinner,
  Row,
  Col,
  Table,
  Badge,
  Modal,
  Form,
  Nav,
  Tab,
} from 'react-bootstrap';
import { FaArrowLeft, FaSync, FaFileExcel, FaUsers } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import { reporteApi } from '../../../services/api/reporteApi';
import cargaComensalesLogiflexApi from '../../../services/api/cargaComensalesLogiflexApi';
import 'react-toastify/dist/ReactToastify.css';

const extraerError = (error, fallback) => {
  const msg = error.response?.data?.detail || error.response?.data?.message || fallback;
  return typeof msg === 'string' ? msg : JSON.stringify(msg);
};

const dnisParaLogiflex = (grupo) => {
  const porCanon = new Map();
  (grupo?.comensales || []).forEach((c) => {
    const raw = String(c.numero_documento_identidad || '').trim();
    const digits = raw.replace(/\D/g, '');
    const canon = digits.replace(/^0+/, '') || digits;
    if (!canon) return;
    const prev = porCanon.get(canon);
    if (!prev || raw.length > prev.length) {
      porCanon.set(canon, raw || digits);
    }
  });
  return [...porCanon.values()];
};

const preferirMantener = (comensales) => {
  const activo = comensales.find((c) => c.activo);
  if (activo) return activo.id_comensal;
  return comensales.reduce((a, b) =>
    a.cantidad_atenciones >= b.cantidad_atenciones ? a : b
  ).id_comensal;
};

const TablaGruposDuplicados = ({
  grupos,
  vacio,
  mantenerPorGrupo,
  setMantenerPorGrupo,
  onConsolidar,
  onConsultarLogiflex,
  consolidandoClave,
  consultandoClave,
}) => {
  if (!grupos.length) {
    return <p className="p-3 mb-0 text-muted small">{vacio}</p>;
  }

  return (
    <div className="table-responsive">
      <Table striped bordered hover size="sm" className="mb-0 align-middle small">
        <thead className="table-light">
          <tr>
            <th>Cliente</th>
            <th>Documento(s)</th>
            <th>Nombre(s)</th>
            <th className="text-center">Reg.</th>
            <th className="text-center">Atenc.</th>
            <th style={{ minWidth: '200px' }}>Conservar</th>
            <th className="text-center" style={{ minWidth: '220px' }}>
              Acción
            </th>
          </tr>
        </thead>
        <tbody>
          {grupos.map((grupo) => {
            const idMantener =
              mantenerPorGrupo[grupo.clave_grupo] ?? grupo.id_comensal_sugerido;
            const puede = grupo.puede_consolidar !== false;
            return (
              <tr key={grupo.clave_grupo}>
                <td>
                  <div className="fw-semibold">{grupo.nombre_cliente || '—'}</div>
                  {grupo.id_cliente != null && (
                    <span className="text-muted">ID {grupo.id_cliente}</span>
                  )}
                </td>
                <td className="text-break" title={grupo.documentos_resumen}>
                  {grupo.documentos_resumen}
                </td>
                <td className="text-break" title={grupo.nombres_resumen}>
                  {grupo.nombres_resumen}
                </td>
                <td className="text-center">
                  <Badge bg="light" text="dark">
                    {grupo.cantidad_comensales}
                  </Badge>
                </td>
                <td className="text-center">
                  <Badge bg={grupo.total_atenciones_grupo > 0 ? 'primary' : 'secondary'}>
                    {grupo.total_atenciones_grupo}
                  </Badge>
                </td>
                <td>
                  {puede ? (
                    <Form.Select
                      size="sm"
                      className="dh-form-control"
                      value={idMantener}
                      onChange={(e) =>
                        setMantenerPorGrupo((prev) => ({
                          ...prev,
                          [grupo.clave_grupo]: parseInt(e.target.value, 10),
                        }))
                      }
                    >
                      {grupo.comensales.map((c) => (
                        <option key={c.id_comensal} value={c.id_comensal}>
                          #{c.id_comensal} — {c.nombre} ({c.cantidad_atenciones} atenc.)
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <span className="text-muted small">{grupo.descripcion}</span>
                  )}
                </td>
                <td className="text-center">
                  <div className="d-flex gap-1 justify-content-center flex-wrap">
                    {puede ? (
                      <Button
                        className="btn-dh-primary btn-sm"
                        disabled={consolidandoClave === grupo.clave_grupo}
                        onClick={() => onConsolidar(grupo, idMantener)}
                      >
                        {consolidandoClave === grupo.clave_grupo ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          'Consolidar'
                        )}
                      </Button>
                    ) : (
                      <Badge bg="secondary" className="align-self-center">
                        Manual
                      </Badge>
                    )}
                    <Button
                      className="btn-dh-secondary btn-sm"
                      disabled={consultandoClave === grupo.clave_grupo}
                      onClick={() => onConsultarLogiflex(grupo)}
                    >
                      {consultandoClave === grupo.clave_grupo ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        'Logiflex'
                      )}
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

const ConsultaComensalesDuplicados = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const currentUser = JSON.parse(localStorage.getItem('user') || 'null');

  const [activeTab, setActiveTab] = useState('doc-nombre');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [tabDocNombre, setTabDocNombre] = useState([]);
  const [tabNomDoc, setTabNomDoc] = useState([]);
  const [tabOtros, setTabOtros] = useState([]);
  const [stats, setStats] = useState(null);

  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroDocumento, setFiltroDocumento] = useState('');
  const [filtroNombre, setFiltroNombre] = useState('');
  const [filtroMinAtenciones, setFiltroMinAtenciones] = useState('');
  const [mantenerPorGrupo, setMantenerPorGrupo] = useState({});
  const [consolidandoClave, setConsolidandoClave] = useState(null);

  const [modalConfirmOpen, setModalConfirmOpen] = useState(false);
  const [grupoPendiente, setGrupoPendiente] = useState(null);
  const [consultandoClave, setConsultandoClave] = useState(null);
  const [modalLogiflex, setModalLogiflex] = useState(null);

  useEffect(() => {
    if (!token || !currentUser) {
      navigate('/');
    }
  }, [navigate, token, currentUser]);

  const buildParams = () => {
    const params = {};
    if (filtroCliente.trim()) params.id_cliente = parseInt(filtroCliente, 10);
    if (filtroDocumento.trim()) params.documento = filtroDocumento.trim();
    if (filtroNombre.trim()) params.nombre = filtroNombre.trim();
    if (filtroMinAtenciones.trim() !== '') {
      params.min_atenciones = parseInt(filtroMinAtenciones, 10);
    }
    return params;
  };

  const aplicarDefaultsMantener = (grupos) => {
    const defaults = {};
    grupos.forEach((g) => {
      defaults[g.clave_grupo] = g.id_comensal_sugerido ?? preferirMantener(g.comensales);
    });
    setMantenerPorGrupo(defaults);
  };

  const buscarDuplicados = useCallback(async () => {
    setLoading(true);
    setTabDocNombre([]);
    setTabNomDoc([]);
    setTabOtros([]);
    setStats(null);
    setMantenerPorGrupo({});
    try {
      const res = await reporteApi.obtenerGruposDuplicadosClasificados(buildParams());
      const body = res.data;
      if (body.success) {
        const g1 = body.mismo_documento_distinto_nombre || [];
        const g2 = body.mismo_nombre_distinto_documento || [];
        const g3 = body.otros_casos || [];
        setTabDocNombre(g1);
        setTabNomDoc(g2);
        setTabOtros(g3);
        setStats(body.estadisticas || null);
        aplicarDefaultsMantener([...g1, ...g2, ...g3]);
        const total = g1.length + g2.length + g3.length;
        if (total === 0) {
          toast.info('No se encontraron grupos de duplicados con los filtros indicados.');
        } else {
          toast.success(`${total} grupo(s) encontrado(s).`);
        }
      } else {
        toast.error(body.message || 'No se pudieron cargar los grupos.');
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        err.message ||
        'Error al buscar duplicados.';
      toast.error(typeof msg === 'string' ? msg : 'Error al buscar duplicados.');
    } finally {
      setLoading(false);
    }
  }, [filtroCliente, filtroDocumento, filtroNombre, filtroMinAtenciones]);

  const exportarExcel = async () => {
    setExporting(true);
    try {
      const response = await reporteApi.descargarComensalesDuplicadosExcel();
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `comensales_duplicados_${new Date().toISOString().split('T')[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel descargado.');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Error al exportar.');
    } finally {
      setExporting(false);
    }
  };

  const consultarLogiflex = async (grupo) => {
    const dnis = dnisParaLogiflex(grupo);
    if (!dnis.length) {
      toast.warning('El grupo no tiene DNI para consultar en Logiflex.');
      return;
    }
    setConsultandoClave(grupo.clave_grupo);
    try {
      const respuestas = await Promise.all(
        dnis.map((dni) => cargaComensalesLogiflexApi.consultarPorDni(dni))
      );
      const vistos = new Set();
      const empleados = [];
      respuestas.forEach((res) => {
        (res.data?.empleados || []).forEach((emp) => {
          const key = `${emp.dni || ''}|${emp.nombre_completo || ''}`;
          if (vistos.has(key)) return;
          vistos.add(key);
          empleados.push(emp);
        });
      });
      setModalLogiflex({
        grupo,
        dnis,
        empleados,
      });
      if (!empleados.length) {
        toast.info('Logiflex no devolvió empleados activos para ese DNI.');
      }
    } catch (err) {
      toast.error(extraerError(err, 'No se pudo consultar Logiflex'));
    } finally {
      setConsultandoClave(null);
    }
  };

  const solicitarConsolidar = (grupo, idMantener) => {
    if (!idMantener) {
      toast.warning('Seleccione el comensal que desea conservar.');
      return;
    }
    const eliminar = grupo.comensales
      .map((c) => c.id_comensal)
      .filter((id) => id !== idMantener);
    if (eliminar.length === 0) {
      toast.warning('El grupo debe tener al menos dos comensales.');
      return;
    }
    setGrupoPendiente({
      grupo,
      idMantener,
      eliminar,
      criterio: grupo.criterio_consolidacion || 'documento',
    });
    setModalConfirmOpen(true);
  };

  const ejecutarConsolidar = async () => {
    if (!grupoPendiente) return;
    const { grupo, idMantener, eliminar, criterio } = grupoPendiente;
    setConsolidandoClave(grupo.clave_grupo);
    setModalConfirmOpen(false);
    try {
      const res = await reporteApi.consolidarComensalesDuplicados({
        id_comensal_mantener: idMantener,
        id_comensales_eliminar: eliminar,
        criterio,
      });
      if (res.data?.success) {
        const msgAuditoria = res.data.id_consolidacion
          ? `${res.data.message || 'Consolidación completada.'} (auditoría #${res.data.id_consolidacion})`
          : res.data.message || 'Consolidación completada.';
        toast.success(msgAuditoria);
        await buscarDuplicados();
      } else {
        toast.error(res.data?.message || 'No se pudo consolidar.');
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        err.message ||
        'Error al consolidar.';
      toast.error(typeof msg === 'string' ? msg : 'Error al consolidar.');
    } finally {
      setConsolidandoClave(null);
      setGrupoPendiente(null);
    }
  };

  const estilosDh = `
    .dh-reporte-bg {
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      min-height: 100vh;
    }
    .dh-main-header {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 2rem 0;
      margin-bottom: 2rem;
      border-radius: 0 0 30px 30px;
      box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
    }
    .dh-main-title { font-size: 2rem; font-weight: 700; margin: 0; }
    .dh-main-subtitle { font-size: 1rem; opacity: 0.9; margin: 0.5rem 0 0 0; }
    .dh-card {
      border: none;
      border-radius: 20px;
      box-shadow: 0 15px 35px rgba(0,0,0,0.1);
      background: white;
    }
    .dh-card-header {
      border-radius: 20px 20px 0 0;
      padding: 1.25rem 1.75rem;
      border: none;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      font-weight: 700;
    }
    .btn-dh-primary {
      background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
      border: none;
      color: white;
      border-radius: 12px;
      padding: 10px 18px;
      font-weight: 600;
    }
    .btn-dh-primary:hover { color: white; filter: brightness(1.06); }
    .btn-dh-secondary {
      background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
      border: none;
      color: white;
      border-radius: 12px;
      padding: 10px 18px;
      font-weight: 600;
    }
    .btn-dh-secondary:hover { color: white; }
    .btn-dh-primary.btn-sm, .btn-dh-secondary.btn-sm {
      padding: 6px 12px;
      border-radius: 10px;
      font-size: 0.82rem;
    }
    .dh-form-label { font-weight: 600; color: #374151; }
    .dh-form-control { border-radius: 10px; }
    .dh-alert-info {
      background: #eef2ff;
      border: 1px solid #c7d2fe;
      border-radius: 12px;
      color: #3730a3;
    }
    .nav-tabs .nav-link { font-weight: 600; }
  `;

  return (
    <>
      <style>{estilosDh}</style>
      <div className="dh-reporte-bg">
        <ToastContainer position="top-right" />
        <div className="dh-main-header">
          <Container>
            <Row className="align-items-center">
              <Col>
                <h1 className="dh-main-title">
                  <FaUsers className="me-2" />
                  Consulta comensales duplicado
                </h1>
                <p className="dh-main-subtitle">
                  Un grupo por fila. Elija el comensal a conservar y consolide en la misma línea.
                  Los servicios de los eliminados se reasignan al conservado.
                </p>
              </Col>
              <Col xs="auto" className="d-flex gap-2">
                <Button
                  variant="light"
                  size="sm"
                  className="rounded-3"
                  onClick={exportarExcel}
                  disabled={exporting}
                >
                  {exporting ? <Spinner size="sm" /> : <FaFileExcel />}
                </Button>
                <Button className="btn-dh-secondary" onClick={() => navigate(-1)}>
                  <FaArrowLeft className="me-2" />
                  Volver
                </Button>
              </Col>
            </Row>
          </Container>
        </div>

        <Container className="pb-5">
          <Card className="dh-card mb-4">
            <Card.Header className="dh-card-header">Filtros</Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={3}>
                  <Form.Label className="dh-form-label">ID cliente</Form.Label>
                  <Form.Control
                    className="dh-form-control"
                    type="number"
                    placeholder="Opcional"
                    value={filtroCliente}
                    onChange={(e) => setFiltroCliente(e.target.value)}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label className="dh-form-label">Documento identidad</Form.Label>
                  <Form.Control
                    className="dh-form-control"
                    placeholder="Coincidencia parcial"
                    value={filtroDocumento}
                    onChange={(e) => setFiltroDocumento(e.target.value)}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label className="dh-form-label">Nombre comensal</Form.Label>
                  <Form.Control
                    className="dh-form-control"
                    placeholder="Contiene…"
                    value={filtroNombre}
                    onChange={(e) => setFiltroNombre(e.target.value)}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label className="dh-form-label">Mín. atenciones</Form.Label>
                  <Form.Control
                    className="dh-form-control"
                    type="number"
                    min={0}
                    placeholder="0"
                    value={filtroMinAtenciones}
                    onChange={(e) => setFiltroMinAtenciones(e.target.value)}
                  />
                </Col>
              </Row>
              <div className="mt-3 d-flex gap-2">
                <Button className="btn-dh-primary" onClick={buscarDuplicados} disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Buscando…
                    </>
                  ) : (
                    <>
                      <FaSync className="me-2" />
                      Buscar duplicados
                    </>
                  )}
                </Button>
              </div>
              <Alert variant="info" className="dh-alert-info mt-3 mb-0 small">
                Proyecto activo en <strong>X-Proyecto-ID</strong>. Cada fila es un grupo
                completo; la consolidación está en la misma línea.
              </Alert>
            </Card.Body>
          </Card>

          {stats && (
            <Card className="dh-card mb-3">
              <Card.Body className="py-2">
                <Badge bg="warning" text="dark" className="me-2">
                  {stats.total_grupos} grupos
                </Badge>
                <Badge bg="secondary" className="me-2">
                  {stats.total_comensales_en_grupos} comensales
                </Badge>
                <Badge bg="info" text="dark" className="me-1">
                  Doc. distinto nombre: {stats.tab_mismo_documento}
                </Badge>
                <Badge bg="info" text="dark" className="me-1">
                  Nombre distinto doc.: {stats.tab_mismo_nombre}
                </Badge>
                <Badge bg="info" text="dark">
                  Otros: {stats.tab_otros}
                </Badge>
              </Card.Body>
            </Card>
          )}

          <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'doc-nombre')}>
            <Nav variant="tabs" className="mb-0">
              <Nav.Item>
                <Nav.Link eventKey="doc-nombre">
                  Mismo documento, distinto nombre
                  {tabDocNombre.length > 0 && (
                    <Badge bg="warning" text="dark" className="ms-2">
                      {tabDocNombre.length}
                    </Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="nom-doc">
                  Mismo nombre, distinto documento
                  {tabNomDoc.length > 0 && (
                    <Badge bg="warning" text="dark" className="ms-2">
                      {tabNomDoc.length}
                    </Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="otros">
                  Otros casos
                  {tabOtros.length > 0 && (
                    <Badge bg="warning" text="dark" className="ms-2">
                      {tabOtros.length}
                    </Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
            </Nav>

            <Card className="dh-card border-top-0 rounded-top-0">
              <Tab.Content>
                <Tab.Pane eventKey="doc-nombre">
                  <TablaGruposDuplicados
                    grupos={tabDocNombre}
                    vacio="Sin grupos: mismo documento de identidad con nombres distintos (mismo cliente)."
                    mantenerPorGrupo={mantenerPorGrupo}
                    setMantenerPorGrupo={setMantenerPorGrupo}
                    onConsolidar={solicitarConsolidar}
                    onConsultarLogiflex={consultarLogiflex}
                    consolidandoClave={consolidandoClave}
                    consultandoClave={consultandoClave}
                  />
                </Tab.Pane>
                <Tab.Pane eventKey="nom-doc">
                  <TablaGruposDuplicados
                    grupos={tabNomDoc}
                    vacio="Sin grupos: misma denominación (nombres/apellidos) con documentos distintos."
                    mantenerPorGrupo={mantenerPorGrupo}
                    setMantenerPorGrupo={setMantenerPorGrupo}
                    onConsolidar={solicitarConsolidar}
                    onConsultarLogiflex={consultarLogiflex}
                    consolidandoClave={consolidandoClave}
                    consultandoClave={consultandoClave}
                  />
                </Tab.Pane>
                <Tab.Pane eventKey="otros">
                  <TablaGruposDuplicados
                    grupos={tabOtros}
                    vacio="Sin otros casos de duplicidad en el proyecto."
                    mantenerPorGrupo={mantenerPorGrupo}
                    setMantenerPorGrupo={setMantenerPorGrupo}
                    onConsolidar={solicitarConsolidar}
                    onConsultarLogiflex={consultarLogiflex}
                    consolidandoClave={consolidandoClave}
                    consultandoClave={consultandoClave}
                  />
                </Tab.Pane>
              </Tab.Content>
            </Card>
          </Tab.Container>
        </Container>

        <Modal
          show={!!modalLogiflex}
          onHide={() => setModalLogiflex(null)}
          centered
          size="lg"
          contentClassName="border-0 shadow rounded-3 overflow-hidden"
        >
          <Modal.Header
            closeButton
            className="text-white border-0"
            style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            <Modal.Title>Logiflex · DNI {modalLogiflex?.dnis?.join(' / ') || '—'}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="pt-4">
            <Alert className="dh-alert-info mb-3">
              Empleados activos en Logiflex para comparar con SGC:{' '}
              <strong>{modalLogiflex?.grupo?.nombres_resumen || '—'}</strong>.
            </Alert>
            {!modalLogiflex?.empleados?.length ? (
              <p className="text-muted mb-0">
                No hay empleados activos en Logiflex para ese documento.
              </p>
            ) : (
              <div className="table-responsive">
                <Table hover size="sm" className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>DNI Logiflex</th>
                      <th>Nombre</th>
                      <th>Empresa</th>
                      <th>Cargo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalLogiflex.empleados.map((emp) => (
                      <tr key={`${emp.dni}-${emp.nombre_completo}`}>
                        <td>{emp.dni || '—'}</td>
                        <td>{emp.nombre_completo || '—'}</td>
                        <td>{emp.empresa || '—'}</td>
                        <td>{emp.cargo || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button className="btn-dh-secondary" onClick={() => setModalLogiflex(null)}>
              Cerrar
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={modalConfirmOpen} onHide={() => setModalConfirmOpen(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirmar consolidación</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {grupoPendiente && (
              <>
                <p>
                  Conservar <strong>#{grupoPendiente.idMantener}</strong> y eliminar:{' '}
                  <strong>
                    {grupoPendiente.eliminar.map((id) => `#${id}`).join(', ')}
                  </strong>
                  .
                </p>
                <p className="mb-0 small text-muted">
                  Criterio: <strong>{grupoPendiente.criterio}</strong>. Se reasignan atenciones,
                  dietas y comedores antes de borrar los duplicados.
                </p>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setModalConfirmOpen(false)}>
              Cancelar
            </Button>
            <Button className="btn-dh-primary" onClick={ejecutarConsolidar}>
              Confirmar
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default ConsultaComensalesDuplicados;
